import React from 'react';
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  useTheme
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Map as MapIcon,
  Assignment as ProjectIcon,
  HealthAndSafety as CareScoreIcon,
  Groups as PatientCohortsIcon,
  Menu as MenuIcon,
  Person as ProfileIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../common/hooks/useAuth';
import { useThemeMode } from '../../common/contexts/ThemeContext';
import CopilotChat from '../CopilotChat';

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 64;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Map View', icon: <MapIcon />, path: '/map' },
  { text: 'Projects', icon: <ProjectIcon />, path: '/project' },
  { text: 'Care Score', icon: <CareScoreIcon />, path: '/care-score' },
  { text: 'Patient Cohorts', icon: <PatientCohortsIcon />, path: '/patient-cohorts' },
];

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleNavigate = (path: string) => navigate(path);
  const handleLogout = () => { logout(); navigate('/'); handleMenuClose(); };
  const handleThemeToggle = () => { toggleTheme(); handleMenuClose(); };
  
  const isDark = mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Top App Bar (minimal) */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.default,
          borderBottom: isDark 
            ? '1px solid rgba(138,43,226,0.3)' 
            : '1px solid rgba(138,43,226,0.2)'
        }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton edge="end" onClick={handleMenuOpen} sx={{ color: theme.palette.primary.main }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(0,180,255,0.15)' }} />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={() => handleNavigate('/profile')}>
              <ListItemIcon><ProfileIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/settings')}>
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleThemeToggle}>
              <ListItemIcon>
                {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{isDark ? 'Light Mode' : 'Dark Mode'}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Left Navigation Rail */}
      <Box
        className="nav-rail"
        sx={{
          position: 'relative',
          '&:hover .drawer-paper': { width: EXPANDED_WIDTH },
          '&:hover ~ .content': {
            marginLeft: `${EXPANDED_WIDTH}px`,
            width: `calc(100% - ${EXPANDED_WIDTH}px)`
          }
        }}
      >
        <Drawer
          variant="permanent"
          PaperProps={{ className: 'drawer-paper' }}
          sx={{
            width: COLLAPSED_WIDTH,
            flexShrink: 0,
            '& .drawer-paper': {
              width: COLLAPSED_WIDTH,
              transition: 'width 0.25s ease',
              overflowX: 'hidden',
              boxSizing: 'border-box',
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              borderRight: isDark 
                ? '1px solid rgba(138,43,226,0.3)' 
                : '1px solid rgba(138,43,226,0.2)'
            }
          }}
        >
          <Toolbar sx={{ justifyContent: 'center', minHeight: 64 }}>
            <MenuIcon sx={{ color: theme.palette.primary.main }} />
          </Toolbar>
          <List sx={{ px: 1, pt: 1 }}>
            {menuItems.map((item) => {
              const selected = location.pathname === item.path;
              return (
                <ListItem
                  button
                  key={item.text}
                  onClick={() => handleNavigate(item.path)}
                  selected={selected}
                  sx={{
                    mb: 1,
                    borderRadius: 1.5,
                    transition: 'all 0.25s ease',
                    ...(selected
                      ? {
                          backgroundColor: 'rgba(0,180,255,0.08)',
                          boxShadow: isDark 
                            ? 'inset 0 0 0 1px rgba(0,180,255,0.25)' 
                            : 'inset 0 0 0 1px rgba(0,180,255,0.2)'
                        }
                      : {
                          '&:hover': {
                            backgroundColor: 'rgba(138,43,226,0.08)',
                            boxShadow: isDark 
                              ? 'inset 0 0 0 1px rgba(138,43,226,0.25)' 
                              : 'inset 0 0 0 1px rgba(138,43,226,0.2)'
                          }
                        })
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: 40, 
                    color: selected 
                      ? theme.palette.primary.main 
                      : isDark 
                        ? 'rgba(255,255,255,0.7)' 
                        : 'rgba(0,0,0,0.6)' 
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      sx: selected
                        ? {
                            background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 700
                          }
                        : { 
                            color: isDark 
                              ? 'rgba(255,255,255,0.85)' 
                              : 'rgba(0,0,0,0.85)', 
                            fontWeight: 600 
                          }
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        className="content"
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${COLLAPSED_WIDTH}px)`,
          marginLeft: `${COLLAPSED_WIDTH}px`,
          transition: 'margin-left 0.25s ease, width 0.25s ease',
          backgroundColor: theme.palette.background.default
        }}
      >
        <Toolbar />
        <Box sx={{ 
          p: 3, 
          minHeight: 'calc(100vh - 64px)', 
          backgroundColor: theme.palette.background.default 
        }}>
          {children}
        </Box>
      </Box>

      {/* Copilot Chat (Right Side) */}
      <CopilotChat />
    </Box>
  );
};

export default MainLayout;